// ImportFromInSpecModal — bring the company's InSpec people into Contacts.
//
// v0.295.0 (2026-11-01). Brett, on the Contacts page: "Do we not have import
// contacts from inspec?", then "yes do the import feature". A company that
// runs both products keeps its people in InSpec; until now they were typed
// into DropPin a second time.
//
// Flow: open → GET /api/import-inspec-contacts reads the company's InSpec
// people live (never from the service worker's cache, never kept on the
// device) → each row says what Import will do with that person → tick →
// POST { ids } → the server re-reads exactly those people from InSpec and
// adds, links or fills in, in one transaction → the counts, in words.
//
// What a row says (the server decides; this box only shows it):
//   linked     "In DropPin"         — ticking fills only its blank fields
//   match      "Links by email"     — one contact here has that email: linked,
//                                     not added twice
//   ambiguous  "Email used twice"   — two or more contacts here share it; it
//                                     cannot be ticked, because picking one
//                                     would be a guess
//   same-email "Email already here" — v0.323.0: a contact here already has
//                                     this email from another InSpec record;
//                                     cannot be ticked, never added twice
//   same-name  "Same name here"     — v0.323.0: a contact here has this name
//                                     and no email to tell them apart; left
//                                     out of Tick all, added only if ticked
//   new        nothing              — added
//
// Every state is named, so "nothing there" never reads like "never looked":
//   loading · list · empty (InSpec has no one) · no match for the search ·
//   no-inspec · no-seat · not-configured · unreachable / inspec-error /
//   unreadable / schema (Try again) · offline (Try again) · signed out (shows
//   nothing: the shell is taking the person to sign in) · any other failure ·
//   importing · done · import refused (busy → Try again, ticks kept) ·
//   import failed (the server rolled back: nothing imported, Try again) ·
//   import unconfirmed (a bare 500 or a commit not confirmed: it may have
//   arrived, so the list is read again and the ticks kept) · import offline
//   (the connection dropped: nothing queued, ticks kept).
//
// DARK MODE: every colour is a theme token; the scrim keeps
// rgba(15,23,42,0.5) like ImportFromInSiteModal.
//
// Props:
//   onBack()                  back to the other import sources
//   onClose()                 close the import altogether
//   onImported({ truncated }) after a good import (truncated = some people
//                             were left out for the Free plan's room)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DownloadCloud, RefreshCw, Check, Link2, Search, AlertTriangle, ChevronLeft } from 'lucide-react';
import { theme, btnPrimary, btnSecondary, input } from '../theme';
import { apiGet, apiSend } from '../api';
import { isSessionEnded } from '../utils/sessionEnd';

const PATH = '/api/import-inspec-contacts';
const MAX_PICK = 500;   // the server's MAX_IDS
const MAX_Q = 100;      // the server's MAX_Q
const SEARCH_MS = 300;

const KIND_LABEL = {
  client: 'Client', consultant: 'Consultant', subcontractor: 'Subcontractor',
  supplier: 'Supplier', builder: 'Builder', staff: 'Staff', other: 'Other',
};

// The GET's failure, as one of the named states.
export function listFailure(err) {
  if (isSessionEnded(err)) return { state: 'ended' };
  const code = err && err.body && err.body.code;
  const message = err && err.body && typeof err.body.error === 'string' ? err.body.error : '';
  if (!err || err instanceof TypeError || !err.status) return { state: 'offline' };
  if (code === 'no-inspec' || code === 'no-seat' || code === 'not-configured') {
    return { state: code, message };
  }
  if (code === 'unreachable' || code === 'inspec-error' || code === 'unreadable' || code === 'schema') {
    return { state: 'inspec-down', message, retry: true };
  }
  if (err.status === 401) return { state: 'signed-out' };
  return { state: 'error', status: err.status, message, retry: true };
}

// The POST's failure. The ticks are kept for every one of these.
//
// v0.296.0: only the server can say "nothing was imported", and only when it
// rolled back (code 'failed', 'busy', InSpec down). A connection that dropped
// mid-send, or a bare status with no word from the route (the platform's own
// 500 when a request runs past its time limit, while the route carries on and
// may yet commit), cannot be told apart from an import that arrived — so those
// say so, and the list is read again to show who is in ({ unconfirmed }).
// Brett's 434-person import answered exactly that bare 500 and the box said
// "Nothing was imported", which nobody could know.
export function importFailure(err) {
  if (isSessionEnded(err)) return { state: 'ended' };
  if (err && err.offline) {
    return { text: 'The connection dropped, so it cannot be told whether the import arrived. Nothing is waiting to send and your ticks are kept. Once you are connected, try again — no one is added twice.', retry: true };
  }
  const code = err && err.body && err.body.code;
  const message = err && err.body && typeof err.body.error === 'string' ? err.body.error : '';
  const status = err && err.status ? err.status : null;
  if (code === 'busy') return { text: message || 'The contacts changed while the import ran. Nothing was imported — try again.', retry: true };
  if (code === 'unreachable' || code === 'inspec-error' || code === 'unreadable' || code === 'schema') {
    return { text: (message || 'InSpec did not answer.') + ' Nothing was imported.', retry: true };
  }
  if (code === 'failed') return { text: message || 'The import could not be finished. Nothing was imported.', retry: true };
  if (code === 'unconfirmed') return { unconfirmed: true, status };
  if (message) return { text: message, retry: false };
  // A bare 4xx is a refusal at the door (no route, too big): the import never began.
  if (status && status < 500) return { text: `The import was refused (${status}). Nothing was imported.`, retry: true };
  return { unconfirmed: true, status };
}

// What the box says after an unconfirmed import, while the list is read again. It
// claims nothing about that read: if it fails, the list says so itself.
export function unconfirmedText(status) {
  return `The import could not be confirmed${status ? ` (${status})` : ''} — it may have finished. Anyone the list marks In DropPin is in. Trying again is safe; no one is added twice.`;
}

// The POST's counts, as sentences. Only the ones that happened.
export function doneLines(c) {
  const n = (k) => Math.max(0, Number(c && c[k]) || 0);
  const ppl = (x) => (x === 1 ? '1 person' : `${x} people`);
  const lines = [];
  if (n('imported')) lines.push({ tone: 'good', text: `${ppl(n('imported'))} added to Contacts.` });
  if (n('linked')) lines.push({ tone: 'good', text: `${ppl(n('linked'))} linked to a contact you already had (same email).` });
  if (n('updated')) lines.push({ tone: 'good', text: `${ppl(n('updated'))} had blank details filled in from InSpec.` });
  if (n('unchanged')) lines.push({ tone: 'plain', text: `${ppl(n('unchanged'))} already up to date — nothing to fill in.` });
  if (n('capped')) lines.push({ tone: 'warn', text: `${ppl(n('capped'))} left out — the Free plan has no more room.` });
  if (n('ambiguous')) lines.push({ tone: 'warn', text: `${ppl(n('ambiguous'))} left out — more than one of your contacts has their email.` });
  if (n('sameEmail')) lines.push({ tone: 'warn', text: `${ppl(n('sameEmail'))} left out — their email is already on a contact, so they were not added twice.` });
  if (n('sameName')) lines.push({ tone: 'warn', text: `${ppl(n('sameName'))} left out — same name as someone else in this import, with no email to tell them apart.` });
  if (n('skipped')) lines.push({ tone: 'warn', text: `${ppl(n('skipped'))} left out — no name in InSpec.` });
  if (n('notFound')) lines.push({ tone: 'warn', text: `${ppl(n('notFound'))} no longer in InSpec, so not brought in.` });
  return lines;
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 200, padding: 16,
};
const panel = {
  background: theme.surface, color: theme.text,
  width: 'min(640px, 100%)', maxHeight: '85vh',
  borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  border: `1px solid ${theme.border}`, boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
};
const header = {
  padding: '14px 18px', borderBottom: `1px solid ${theme.border}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
};
const headerTitle = { margin: 0, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 };
const closeBtn = {
  border: 0, background: 'transparent', cursor: 'pointer',
  fontSize: 20, color: theme.textLight, lineHeight: 1, padding: '2px 4px',
};
const toolbar = {
  padding: '10px 14px', borderBottom: `1px solid ${theme.border}`,
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
};
const searchWrap = { position: 'relative', width: 280, maxWidth: '100%' };
const searchIcon = { position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: theme.textLight, pointerEvents: 'none' };
const body = { overflowY: 'auto', flex: 1, padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 };
const footer = {
  padding: '10px 18px', borderTop: `1px solid ${theme.border}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
};
const centreState = {
  padding: '32px 20px', textAlign: 'center', color: theme.textLight, fontSize: 13,
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
};
const noteBox = {
  marginTop: 4, padding: '10px 12px', borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.bg, color: theme.text, fontSize: 12.5, maxWidth: 400,
};
const errorBox = {
  margin: '4px 0', padding: '12px 14px', borderRadius: 8, border: `1px solid ${theme.danger}`,
  background: theme.bg, color: theme.danger, fontSize: 13,
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
};
// An import that may have finished is not an error: a warning edge, the words in
// text ink (the warning orange is too light to read as body text).
const unsureBox = { ...errorBox, border: `1px solid ${theme.warn}`, color: theme.text };
const rowBase = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
  border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.surface,
};
// The name and the marks share a line while both fit; on a phone the marks
// drop under the name instead of cutting it to "Sam Ok…".
const rowMain = { flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12, rowGap: 4 };
const rowText = { flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 };
const rowTags = { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 };
const rowName = { fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const rowMeta = { fontSize: 11.5, color: theme.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const kindBadge = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
  border: `1px solid ${theme.border}`, borderRadius: 4, padding: '1px 6px',
  color: theme.textLight, flexShrink: 0,
};
const mark = { fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap' };
const retryBtn = { ...btnSecondary, fontSize: 13, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 };
const linkBtn = {
  border: 0, background: 'transparent', color: theme.brand, cursor: 'pointer',
  fontSize: 12.5, fontWeight: 600, padding: '4px 2px',
};

function Spinner() {
  return (
    <>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block', width: 18, height: 18, borderRadius: '50%',
          border: `2px solid ${theme.border}`, borderTopColor: theme.brand,
          animation: 'inspec-spin 0.7s linear infinite',
        }}
      />
      <style>{`@keyframes inspec-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// v0.323.0 - a person who would only be skipped cannot be ticked.
export function canTick(status) {
  return status !== 'ambiguous' && status !== 'same-email';
}

function StatusMark({ status }) {
  if (status === 'linked') {
    return <span style={{ ...mark, color: theme.success }} title="Already in Contacts from InSpec. Ticking it fills in only its blank details."><Check size={13} /> In DropPin</span>;
  }
  if (status === 'match') {
    return <span style={{ ...mark, color: theme.text }} title="One of your contacts has this email. It is linked to them, not added twice."><Link2 size={13} /> Links by email</span>;
  }
  if (status === 'ambiguous') {
    return <span style={{ ...mark, color: theme.warn }} title="More than one of your contacts has this email, so it cannot be told which one this is."><AlertTriangle size={13} /> Email used twice</span>;
  }
  if (status === 'same-email') {
    return <span style={{ ...mark, color: theme.textLight }} title="A contact here already has this email (InSpec has this person on another record). They are not added twice."><Check size={13} /> Email already here</span>;
  }
  if (status === 'same-name') {
    return <span style={{ ...mark, color: theme.warn }} title="A contact here has this name and no email to tell them apart. Check Contacts first - ticking adds a new contact. Tick all leaves it out."><AlertTriangle size={13} /> Same name here</span>;
  }
  return null;
}

export default function ImportFromInSpecModal({ onBack, onClose, onImported }) {
  const [q, setQ] = useState('');
  const [view, setView] = useState({ state: 'loading' });   // see listFailure for the failures
  const [data, setData] = useState(null);                    // { people, truncated, existing, capLeft }
  const [picked, setPicked] = useState(() => new Set());     // externalIds, kept across searches
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);              // { text, retry }
  const [done, setDone] = useState(null);                    // the POST's counts
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async (words) => {
    const mine = ++seq.current;
    setView({ state: 'loading' });
    try {
      const w = String(words || '').trim().slice(0, MAX_Q);
      const r = await apiGet(PATH + (w ? `?q=${encodeURIComponent(w)}` : ''));
      if (!alive.current || mine !== seq.current) return;      // a newer search answered for itself
      const people = Array.isArray(r && r.people) ? r.people.filter((p) => p && p.externalId) : [];
      setData({
        people,
        truncated: !!(r && r.truncated),
        existing: Number(r && r.existing) || 0,
        capLeft: r && r.capLeft != null ? Math.max(0, Number(r.capLeft) || 0) : null,
        q: w,
      });
      setView({ state: 'list' });
    } catch (err) {
      if (!alive.current || mine !== seq.current) return;
      setView(listFailure(err));
    }
  }, []);

  // First read at once; a search waits for the typing to stop.
  const firstRead = useRef(true);
  useEffect(() => {
    if (firstRead.current) { firstRead.current = false; load(q); return undefined; }
    const t = setTimeout(() => load(q), SEARCH_MS);
    return () => clearTimeout(t);
  }, [q, load]);

  const close = useCallback(() => { if (!sending) onClose(); }, [sending, onClose]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const people = (data && data.people) || [];
  // v0.323.0 - Tick all never takes a same-name person: that is a call to make one at a time.
  const selectable = people.filter((p) => canTick(p.status) && p.status !== 'same-name');
  const allShownPicked = selectable.length > 0 && selectable.every((p) => picked.has(p.externalId));
  const tooMany = picked.size > MAX_PICK;
  // Room on Free is used only by people who would be ADDED; a link or a fill
  // adds no one. Counted over the ticks this list can see.
  const capLeft = data ? data.capLeft : null;
  const pickedNew = people.filter((p) => p.status === 'new' && picked.has(p.externalId)).length;

  const toggle = (id) => {
    setSendErr(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllShown = () => {
    setSendErr(null);
    setPicked((prev) => {
      const next = new Set(prev);
      if (allShownPicked) selectable.forEach((p) => next.delete(p.externalId));
      else selectable.forEach((p) => next.add(p.externalId));
      return next;
    });
  };

  const runImport = async () => {
    if (!picked.size || tooMany || sending) return;
    setSending(true);
    setSendErr(null);
    try {
      const r = await apiSend('POST', PATH, { ids: [...picked] }, { noQueue: true });
      if (!alive.current) return;
      setDone(r || {});
      setPicked(new Set());
      if (typeof onImported === 'function') onImported({ truncated: Number(r && r.capped) > 0 });
    } catch (err) {
      if (!alive.current) return;
      const f = importFailure(err);
      if (f.state === 'ended') setView({ state: 'ended' });
      else if (f.unconfirmed) {
        // It may have arrived: the ticks stay (a second Bring in links or
        // fills, never adds twice) and the list is read again, so each row
        // says what is true now. Contacts behind the box reads again too.
        setSendErr({ text: unconfirmedText(f.status), retry: true, unconfirmed: true });
        load(q);
        if (typeof onImported === 'function') onImported({ truncated: false });
      } else setSendErr(f);
    } finally {
      if (alive.current) setSending(false);
    }
  };

  const backToList = () => { setDone(null); load(q); };

  // Signed out: the shell is taking the person to sign in. Show nothing.
  if (view.state === 'ended') return null;

  const title = (
    <h3 id="inspec-import-title" style={headerTitle}>
      <DownloadCloud size={18} style={{ color: theme.brand }} aria-hidden="true" />
      Import from InSpec
    </h3>
  );

  let content;
  if (done) {
    const lines = doneLines(done);
    content = (
      <div style={{ padding: '6px 2px' }} data-state="done">
        <div style={{ ...rowBase, borderColor: theme.success, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={16} style={{ color: theme.success }} /> Import finished
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ fontSize: 13, color: l.tone === 'warn' ? theme.warn : theme.text }}>{l.text}</div>
          ))}
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2 }}>
            Nothing you had typed into DropPin was changed, and notes were not touched.
          </div>
        </div>
      </div>
    );
  } else if (view.state === 'loading') {
    content = (
      <div style={centreState} data-state="loading">
        <Spinner />
        {q.trim() ? `Searching InSpec for “${q.trim()}”…` : 'Reading your company’s people from InSpec…'}
      </div>
    );
  } else if (view.state === 'list' && people.length === 0) {
    content = data && data.q ? (
      <div style={centreState} data-state="no-match">
        No one in InSpec matches “{data.q}”.
        <button type="button" style={linkBtn} onClick={() => setQ('')}>Clear the search</button>
      </div>
    ) : (
      <div style={centreState} data-state="empty">
        InSpec has no people for your company yet.
        <div style={noteBox}>People added to InSpec’s contacts show here. Archived people and companies are left out.</div>
      </div>
    );
  } else if (view.state === 'list') {
    content = people.map((p) => {
      const can = canTick(p.status);
      const on = picked.has(p.externalId);
      const meta = [p.company && p.company !== p.name ? p.company : '', p.email, p.position || p.trade].filter(Boolean).join(' · ');
      return (
        <label
          key={p.externalId}
          data-person={p.externalId}
          data-status={p.status}
          style={{
            ...rowBase,
            cursor: can ? 'pointer' : 'default',
            borderColor: on ? theme.brand : theme.border,
            opacity: can ? 1 : 0.75,
          }}
        >
          <input
            type="checkbox"
            checked={on}
            disabled={!can || sending}
            onChange={() => toggle(p.externalId)}
            style={{ width: 16, height: 16, accentColor: theme.brand, flexShrink: 0, margin: 0 }}
            aria-label={`Bring in ${p.name || p.company || 'this person'}`}
          />
          <span style={rowMain}>
            <span style={rowText}>
              <span style={rowName}>{p.name || p.company || 'No name'}</span>
              {meta && <span style={rowMeta}>{meta}</span>}
            </span>
            <span style={rowTags}>
              {KIND_LABEL[p.kind] && <span style={kindBadge}>{KIND_LABEL[p.kind]}</span>}
              <StatusMark status={p.status} />
            </span>
          </span>
        </label>
      );
    });
  } else if (view.state === 'no-inspec' || view.state === 'no-seat' || view.state === 'not-configured') {
    const note = {
      'no-inspec': 'People can still come in from a file or this device — go Back.',
      'no-seat': 'The InSpec contact list belongs to your company’s InSpec office. Ask whoever manages your InSpec seats, or bring people in from a file.',
      'not-configured': 'This is a setting on the server, not something on your side.',
    }[view.state];
    const fallback = {
      'no-inspec': 'Your company does not run InSpec, so there is no one to bring in.',
      'no-seat': 'Bringing people in from InSpec needs an InSpec seat in your company.',
      'not-configured': 'Bringing people in from InSpec is not set up on this server yet.',
    }[view.state];
    content = (
      <div style={centreState} data-state={view.state}>
        <div style={{ color: theme.text, fontSize: 13.5, fontWeight: 600 }}>{view.message || fallback}</div>
        <div style={noteBox}>{note}</div>
      </div>
    );
  } else {
    const text = {
      offline: 'No connection. The InSpec list is read live and never kept on this device, so it needs a connection.',
      'signed-out': 'InSpec refused the request: this sign-in is not allowed to read the list.',
      'inspec-down': view.message || 'InSpec did not answer.',
      error: `The InSpec list could not be read${view.status ? ` (${view.status})` : ''}.${view.message ? ` ${view.message}` : ''}`,
    }[view.state] || 'The InSpec list could not be read.';
    content = (
      <div style={errorBox} data-state={view.state}>
        {text}
        <button type="button" style={retryBtn} onClick={() => load(q)}><RefreshCw size={13} /> Try again</button>
      </div>
    );
  }

  const listing = !done && view.state === 'list' && people.length > 0;
  // Search and Bring in show only where a list is (or is coming): a screen that
  // can never import (no seat, InSpec down, offline, no one in InSpec) shows
  // Back only. A search with no match keeps both — the ticks are still there.
  const nobodyInInSpec = view.state === 'list' && people.length === 0 && !(data && data.q);
  const canSearch = !done && !nobodyInInSpec && (view.state === 'list' || view.state === 'loading');

  return (
    <div style={overlay} onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspec-import-title"
        style={panel}
        onClick={(e) => e.stopPropagation()}
        data-inspec-import
      >
        <div style={header}>
          {title}
          <button type="button" onClick={close} disabled={sending} style={{ ...closeBtn, cursor: sending ? 'default' : 'pointer' }} aria-label="Close">×</button>
        </div>

        {canSearch && (
          <div style={toolbar}>
            <div style={searchWrap}>
              <Search size={14} style={searchIcon} aria-hidden="true" />
              <input
                type="search"
                value={q}
                maxLength={MAX_Q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, company or email"
                aria-label="Search InSpec people"
                style={{ ...input, paddingLeft: 30 }}
                disabled={sending}
              />
            </div>
            {listing && selectable.length > 0 && (
              <button type="button" style={linkBtn} onClick={toggleAllShown} disabled={sending}>
                {allShownPicked ? 'Untick all shown' : `Tick all shown (${selectable.length})`}
              </button>
            )}
          </div>
        )}

        <div style={body} aria-busy={view.state === 'loading' || sending}>
          {listing && data.truncated && (
            <div style={{ fontSize: 12, color: theme.textLight, padding: '0 2px 4px' }} data-note="truncated">
              Showing the first {people.length.toLocaleString('en-AU')} — search to find anyone else.
            </div>
          )}
          {content}
        </div>

        {sendErr && (
          <div style={{ padding: '0 14px 10px' }}>
            <div style={{ ...(sendErr.unconfirmed ? unsureBox : errorBox), margin: 0 }} data-state={sendErr.unconfirmed ? 'import-unconfirmed' : 'import-failed'}>
              {sendErr.unconfirmed && <AlertTriangle size={16} color={theme.warn} aria-hidden="true" />}
              {sendErr.text}
              {sendErr.retry && (
                <button type="button" style={retryBtn} onClick={runImport} disabled={sending}><RefreshCw size={13} /> Try again</button>
              )}
            </div>
          </div>
        )}

        <div style={footer}>
          {done ? (
            <>
              <button type="button" style={btnSecondary} onClick={backToList}>Back to the list</button>
              <button type="button" style={btnPrimary} onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button type="button" style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={onBack} disabled={sending}>
                <ChevronLeft size={14} /> Back
              </button>
              {canSearch && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {capLeft != null && view.state === 'list' && (
                    <span style={{ fontSize: 12, color: pickedNew > capLeft ? theme.warn : theme.textLight }} data-note="cap">
                      {pickedNew > capLeft
                        ? `Free plan: room for ${capLeft} more — ${pickedNew - capLeft} will be left out`
                        : `Free plan: room for ${capLeft} more`}
                    </span>
                  )}
                  {tooMany && <span style={{ fontSize: 12, color: theme.warn }} data-note="too-many">Up to {MAX_PICK} at a time</span>}
                  {sending && <Spinner />}
                  <button
                    type="button"
                    style={{ ...btnPrimary, opacity: !picked.size || tooMany || sending ? 0.55 : 1, cursor: !picked.size || tooMany || sending ? 'default' : 'pointer' }}
                    onClick={runImport}
                    disabled={!picked.size || tooMany || sending}
                    data-action="import"
                  >
                    {sending
                      ? `Bringing in ${picked.size}…`
                      : picked.size ? `Bring in ${picked.size === 1 ? '1 person' : `${picked.size} people`}` : 'Tick people to bring in'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
